import React from 'react';

import { cn } from '@/lib/utils';

/** How strongly a piece of evidence speaks for a match. `none` = nothing to go on. */
export type ScoreTone = 'good' | 'fair' | 'poor' | 'none';

const DOT: Record<ScoreTone, string> = {
    good: 'bg-success',
    fair: 'bg-warning',
    poor: 'bg-destructive',
    none: 'bg-muted-foreground/40',
};

export interface ScoreChipProps {
    /** What is scored: "EAN", "Name", "Look", "Overall"… */
    label: string;
    /**
     * A 0..1 score, shown as a whole percentage ("82%"), or a ready reading
     * ("93", "agrees", "—"). Absent shows the label alone — an EAN that matched
     * is a yes, not a score.
     */
    value?: React.ReactNode;
    /**
     * A verdict dot, for the people whose job is to decide (super-admin
     * curation). Omit it for a neutral chip: shown to a retailer, a score is
     * evidence to weigh, and tinting 82% green would tell them to stop reading.
     */
    tone?: ScoreTone;
    /** The one chip of a set that carries the verdict: a hairline darker, the number in the foreground colour. */
    emphasis?: boolean;
    /** The longer explanation, on hover. */
    title?: string;
    size?: 'sm' | 'lg';
    className?: string;
    testId?: string;
}

function reading(value: React.ReactNode): React.ReactNode {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` : null;
    }
    return value === '' ? null : value;
}

/**
 * One piece of evidence as a small hairline chip — label, then reading. The
 * Ardealy Network shows several side by side (overall, name, look, EAN);
 * anything else that shows a confidence should use this rather than inventing
 * its own pill.
 */
export const ScoreChip: React.FC<ScoreChipProps> = ({ label, value, tone, emphasis, title, size = 'sm', className, testId }) => {
    const shown = reading(value);
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 rounded-md border bg-background whitespace-nowrap',
                size === 'lg' ? 'h-8 px-2.5 text-sm' : 'h-6 px-2 text-xs',
                emphasis && 'border-foreground/25',
                className,
            )}
            title={title}
            data-tone={tone}
            data-testid={testId}
        >
            {tone && <span className={cn('size-1.5 shrink-0 rounded-full', DOT[tone])} aria-hidden />}
            <span className={emphasis ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
            {shown !== null && shown !== undefined && <span className="font-medium tabular-nums text-foreground">{shown}</span>}
        </span>
    );
};

/**
 * The tone of a 0..1 similarity against the two thresholds of the model that
 * produced it (`same` = almost surely the same product, `similar` = looks alike).
 */
export function scoreTone(value: number | null | undefined, thresholds: { same: number; similar: number }): ScoreTone {
    if (value == null || Number.isNaN(value)) return 'none';
    if (value >= thresholds.same) return 'good';
    if (value >= thresholds.similar) return 'fair';
    return 'poor';
}

/** 0.934 → "93". Scores read as whole percentages; the chip's label says what of. */
export function formatScore(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '—';
    return String(Math.round(Math.max(0, Math.min(1, value)) * 100));
}
